import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ExternalLink,
  KeyRound,
  Link2,
  Plus,
  RotateCcw,
  Search,
  UserX,
  Users,
} from 'lucide-react';
import {
  SHIFT_SHORT,
  fmtPhone,
  phoneDigits,
  type EmploymentType,
  type PasskeyHome,
} from '@siva/shared';
import { api, post } from '../../lib/api.js';
import type { PublicUser } from '../../lib/auth.js';
import { errorMessage } from '../../lib/passkey.js';
import {
  Alert,
  fmtDate,
  InviteLinkCard,
  passkeyHomeClass,
  passkeyHomeLabel,
  phoneTakenMessage,
  shiftLabel,
  Spinner,
  type InviteLink,
} from '../../lib/ui.js';
import { photoSrc } from '../../lib/PhotoPicker.js';
import { PhotoViewer } from '../../lib/PhotoViewer.js';
import { Avatar, Badge, Card, EmptyState } from '../../lib/components.js';
import { usePhotos } from '../photos.js';
import { Menu } from '../../lib/Menu.js';
import { SlideOver } from '../../lib/SlideOver.js';
import { useConfirm } from '../../lib/Modal.js';
import {
  emptyWorkerForm,
  WorkerForm,
  workerPayload,
  type WorkerFieldErrors,
  type WorkerFieldKey,
  type WorkerFormValues,
  type Designation,
} from '../WorkerForm.js';
import { t } from '../../i18n/index.js';

type WorkerRow = PublicUser & {
  passkey: {
    registered: boolean;
    since?: string;
    last_used_at?: string | null;
    device?: string | null;
    /** What the authenticator said it was when the passkey was made. */
    home?: PasskeyHome;
  };
  pending_reregistration: boolean;
  open_invite: { purpose: string; expires_at: string } | null;
  month_minutes?: number;
  /** When their photograph was set, or null. The photographs themselves are fetched one by one. */
  photo_updated_at?: string | null;
  /** Whether, not what: the list never carries the account itself. */
  has_bank_details?: boolean;
  /** The labels of what is missing, so the badge can say. */
  bank_missing?: string[];
};

type Rereg = {
  id: string;
  reason: string | null;
  note: string | null;
  created_at: string;
  worker: {
    id: string;
    name: string;
    phone: string | null;
    employment_type: EmploymentType | null;
  };
};

function SortHead({
  k,
  label,
  sort,
  onSort,
}: {
  k: SortKey;
  label: string;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (k: SortKey) => void;
}) {
  return (
    <th aria-sort={sort.key === k ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="th-sort" onClick={() => onSort(k)}>
        {label}
        {sort.key === k &&
          (sort.dir === 'asc' ? (
            <ArrowUp size={13} aria-hidden="true" />
          ) : (
            <ArrowDown size={13} aria-hidden="true" />
          ))}
      </button>
    </th>
  );
}

type SortKey = 'name' | 'type' | 'join_date';

/** Map a 409 from the API onto the form field it belongs to. */
function fieldErrorOf(err: unknown): { field: WorkerFieldKey; message: string } | null {
  const phone = phoneTakenMessage(err);
  if (phone) return { field: 'phone', message: phone };
  return null;
}

export function WorkersPage() {
  const { photoOf } = usePhotos();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState<WorkerRow[] | null>(null);
  const [pending, setPending] = useState<Rereg[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [link, setLink] = useState<{ link: InviteLink; note?: string } | null>(null);
  // The invite card (QR and link) appears at the top of the page, but the admin is usually further
  // down — at the row they pressed New Link on, or wherever the Add form was opened. The owner had
  // to scroll up to find it (15 October 2026); the page now goes to it.
  const linkRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (link) linkRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [link]);
  const [error, setError] = useState<string | null>(null);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<WorkerFormValues>(emptyWorkerForm);
  const [fieldErrors, setFieldErrors] = useState<WorkerFieldErrors>({});
  const [nextCode, setNextCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** A face from the list, open at full size: { src, name }. */
  const [viewing, setViewing] = useState<{ src: string; name: string } | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'name',
    dir: 'asc',
  });

  const load = useCallback(async () => {
    try {
      const [w, r] = await Promise.all([
        api<{ workers: WorkerRow[] }>(`/admin/workers${showInactive ? '?include_inactive=1' : ''}`),
        api<{ requests: Rereg[] }>('/admin/reregistrations?status=pending'),
      ]);
      setWorkers(w.workers);
      setPending(r.requests);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [showInactive]);
  useEffect(() => {
    // The admin-managed list from Settings. A failure here is not worth a message: the dropdown
    // simply has nothing in it, and the rest of the form still works.
    api<{ designations: Designation[] }>('/admin/designations')
      .then((r) => setDesignations(r.designations))
      .catch(() => {});
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openForm = () => {
    setForm(emptyWorkerForm());
    setFieldErrors({});
    setAdding(true);
    // Show the code this worker will get. It is a peek, not a draw, so opening the panel and
    // closing it again costs nothing; if another admin saves first, the code allocated is the
    // next one after theirs, which is why the field cannot be edited.
    setNextCode(null);
    api<{ code: string }>('/admin/workers/next-code')
      .then((r) => setNextCode(r.code))
      .catch(() => setNextCode(null));
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      const fe = fieldErrorOf(err);
      if (fe) setFieldErrors((e) => ({ ...e, [fe.field]: fe.message }));
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const create = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await post<{ worker: PublicUser; invite: InviteLink }>(
        '/admin/workers',
        workerPayload(form, 'create'),
      );
      setLink({ link: r.invite, note: t('worker_created') });
      setAdding(false);
      setForm(emptyWorkerForm());
      setFieldErrors({});
    });
  };

  const newLink = (w: WorkerRow) =>
    void run(async () => {
      const r = await post<{ invite: InviteLink }>(`/admin/workers/${w.id}/invite`);
      setLink({ link: r.invite, note: t('link_open_invalidates') });
    });

  const reactivate = (w: WorkerRow) =>
    void run(async () => {
      await post(`/admin/workers/${w.id}/reactivate`);
    });

  const deactivate = async (w: WorkerRow) => {
    const ok = await confirm({
      title: t('worker_deactivate'),
      body: t('worker_deactivate_confirm', { name: w.name }),
      confirmLabel: t('worker_deactivate'),
      danger: true,
    });
    if (!ok) return;
    void run(async () => {
      await post(`/admin/workers/${w.id}/deactivate`);
    });
  };

  const approve = (r: Rereg) =>
    void run(async () => {
      const res = await post<{ invite: InviteLink }>(`/admin/reregistrations/${r.id}/approve`);
      setLink({ link: res.invite });
    });
  const reject = (r: Rereg) =>
    void run(async () => {
      await post(`/admin/reregistrations/${r.id}/reject`, {});
    });

  const counts = useMemo(() => {
    const list = workers ?? [];
    const active = list.filter((w) => w.active);
    return {
      total: list.length,
      full: active.filter((w) => w.employment_type === 'full_time').length,
      part: active.filter((w) => w.employment_type === 'part_time').length,
    };
  }, [workers]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const digits = phoneDigits(q);
    const list = (workers ?? []).filter((w) => {
      if (!q) return true;
      return (
        w.name.toLowerCase().includes(q) ||
        (w.employee_code ?? '').toLowerCase().includes(q) ||
        (digits.length >= 3 && phoneDigits(w.phone ?? '').includes(digits))
      );
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    const by: Record<SortKey, (w: WorkerRow) => string | number> = {
      name: (w) => w.name.toLowerCase(),
      type: (w) => w.employment_type ?? 'zz',
      join_date: (w) => w.join_date ?? '',
    };
    return [...list].sort((a, b) => {
      const x = by[sort.key](a);
      const y = by[sort.key](b);
      if (x === y) return a.name.localeCompare(b.name);
      return (x < y ? -1 : 1) * dir;
    });
  }, [workers, query, sort]);

  const sortBy = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  return (
    <div className="stack">
      {link && (
        <div ref={linkRef} className="invite-anchor">
          <InviteLinkCard
            link={link.link}
            {...(link.note ? { note: link.note } : {})}
            onClose={() => setLink(null)}
          />
        </div>
      )}

      {pending.length > 0 && (
        <Card title={t('rereg_pending_title')}>
          <ul className="list">
            {pending.map((r) => (
              <li key={r.id} className="row between">
                <span className="person">
                  <Avatar name={r.worker.name} size={30} photoUrl={photoOf(r.worker.id)} />
                  <span>
                    <strong>{r.worker.name}</strong> · {fmtPhone(r.worker.phone)} ·{' '}
                    {r.reason ? t(`rereg_reason_${r.reason as 'new_phone'}`) : ''}
                    {r.note ? ` — ${r.note}` : ''}
                    <br />
                    <span className="muted">
                      {t('rereg_requested_at', { date: fmtDate(r.created_at) })}
                    </span>
                  </span>
                </span>
                <span className="row">
                  <button type="button" onClick={() => approve(r)} disabled={busy}>
                    {t('approve')}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => reject(r)}
                    disabled={busy}
                  >
                    {t('reject')}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title={t('workers_title')}
        subtitle={
          workers
            ? t('workers_count', { n: counts.total, ft: counts.full, pt: counts.part })
            : undefined
        }
        actions={
          <span className="row">
            <span className="search table-search" role="search">
              <Search size={15} className="search-icon" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('workers_search')}
                aria-label={t('workers_search')}
              />
            </span>
            <label className="inline">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              {t('workers_show_inactive')}
            </label>
            <button type="button" onClick={() => void openForm()}>
              <Plus size={16} aria-hidden="true" />
              {t('workers_add')}
            </button>
          </span>
        }
      >
        {error && <Alert>{error}</Alert>}
        {viewing && (
          <PhotoViewer src={viewing.src} name={viewing.name} onClose={() => setViewing(null)} />
        )}

        {!workers ? (
          <Spinner />
        ) : shown.length === 0 ? (
          <EmptyState icon={Users} text={query ? t('workers_no_match') : t('workers_none')} />
        ) : (
          <div className="table-wrap">
            {/* The stacked cards have no column headers to tap, so the sort moves here. */}
            <label className="sort-mobile">
              <span>{t('workers_sort_by')}</span>
              <select
                value={sort.key}
                onChange={(e) => setSort({ key: e.target.value as SortKey, dir: 'asc' })}
              >
                <option value="name">{t('worker_name')}</option>
                <option value="type">{t('worker_type')}</option>
                <option value="join_date">{t('worker_join_date')}</option>
                <option value="month">{t('workers_col_hours')}</option>
              </select>
            </label>
            <table className="stacks workers">
              <thead>
                <tr>
                  <SortHead sort={sort} onSort={sortBy} k="name" label={t('worker_name')} />
                  <th>{t('worker_phone')}</th>
                  <SortHead sort={sort} onSort={sortBy} k="type" label={t('worker_type')} />
                  <SortHead
                    sort={sort}
                    onSort={sortBy}
                    k="join_date"
                    label={t('worker_join_date')}
                  />
                  <th>{t('worker_passkey')}</th>
                  <th>{t('worker_status')}</th>
                  <th>{t('worker_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((w) => (
                  <tr
                    key={w.id}
                    className={`clickable ${w.active ? '' : 'inactive'}`}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, a')) return;
                      navigate(`/workers/${w.id}`);
                    }}
                  >
                    <td data-label={t('worker_name')} className="cell-name">
                      <span className="name-cell">
                        <Avatar
                          name={w.name}
                          size={30}
                          photoUrl={photoSrc(`/admin/workers/${w.id}/photo`, w.photo_updated_at)}
                          onOpen={() => {
                            const src = photoSrc(
                              `/admin/workers/${w.id}/photo`,
                              w.photo_updated_at,
                            );
                            if (src) setViewing({ src, name: w.name });
                          }}
                        />
                        <span>
                          <Link to={`/workers/${w.id}`}>{w.name}</Link>
                          {w.employee_code && (
                            <>
                              <br />
                              <small className="muted">{w.employee_code}</small>
                            </>
                          )}
                          {/* Something to chase, not something to stop for: a worker without bank
                              details still punches, and still gets an advice. A short line under
                              the code since 15 October — the full list of fields on every row
                              was most of what the page said. */}
                          {w.active && w.has_bank_details === false && (
                            <small
                              className="bank-missing"
                              title={
                                w.bank_missing?.length
                                  ? t('bank_missing_fields', { fields: w.bank_missing.join(', ') })
                                  : undefined
                              }
                            >
                              {' · '}
                              {!w.bank_missing?.length || w.bank_missing.length >= 4
                                ? t('bank_admin_missing')
                                : t('bank_missing_fields', { fields: w.bank_missing.join(', ') })}
                            </small>
                          )}
                        </span>
                        {w.pending_reregistration && (
                          <span className="badge warn">{t('worker_pending_rereg')}</span>
                        )}
                      </span>
                    </td>
                    <td className="nowrap" data-label={t('worker_phone')}>
                      {fmtPhone(w.phone)}
                    </td>
                    <td data-label={t('worker_type')}>
                      {w.employment_type && (
                        <Badge
                          tone={w.employment_type === 'full_time' ? 'blue' : 'amber'}
                          className="no-ml"
                        >
                          {t(w.employment_type)}
                        </Badge>
                      )}
                      <Badge tone="neutral" title={shiftLabel(w.shift)}>
                        {SHIFT_SHORT[w.shift]}
                      </Badge>
                    </td>
                    <td className="nowrap" data-label={t('worker_join_date')}>
                      {w.join_date ? fmtDate(w.join_date, false) : ''}
                    </td>
                    <td data-label={t('worker_passkey')}>
                      {w.passkey.registered ? (
                        <Badge
                          tone="green"
                          className="no-ml"
                          title={t('passkey_registered_since', {
                            date: fmtDate(w.passkey.since, false),
                          })}
                        >
                          {t('worker_passkey_ok')}
                        </Badge>
                      ) : null}
                      {w.passkey.registered && w.passkey.device && (
                        <>
                          <br />
                          <small className="muted device-label">{w.passkey.device}</small>
                        </>
                      )}
                      {/* The line above is what the browser said at enrolment; this one is what
                          the passkey itself said, which a browser setting cannot change. */}
                      {w.passkey.registered && w.passkey.home && (
                        <>
                          <br />
                          <small className={passkeyHomeClass(w.passkey.home)}>
                            {/* Labelled, because the line above is the browser that enrolled
                                them and this one is where the key lives. They can disagree —
                                a passkey made on an Android phone while the invite was open
                                on an iPhone — and two bare lines then read as a contradiction. */}
                            {t('passkey_home_short', { where: passkeyHomeLabel(w.passkey.home) })}
                          </small>
                        </>
                      )}
                      {w.passkey.registered ? null : (
                        <>
                          <Badge tone="neutral" className="no-ml">
                            {t('passkey_not_registered')}
                          </Badge>
                          {w.open_invite && (
                            <>
                              <br />
                              <small className="muted">
                                {t('worker_invite_open', {
                                  date: fmtDate(w.open_invite.expires_at),
                                })}
                              </small>
                            </>
                          )}
                        </>
                      )}
                    </td>
                    <td data-label={t('worker_status')}>
                      <Badge tone={w.active ? 'green' : 'neutral'} className="no-ml">
                        {w.active ? t('worker_active') : t('worker_inactive')}
                      </Badge>
                    </td>
                    <td className="actions" data-label={t('worker_actions')}>
                      <Menu
                        label={t('worker_actions_for', { name: w.name })}
                        items={
                          w.active
                            ? [
                                w.passkey.registered
                                  ? {
                                      label: t('worker_reregister'),
                                      icon: <KeyRound size={15} aria-hidden="true" />,
                                      onSelect: () => newLink(w),
                                    }
                                  : {
                                      label: t('worker_new_link'),
                                      icon: <Link2 size={15} aria-hidden="true" />,
                                      onSelect: () => newLink(w),
                                    },
                                {
                                  label: t('worker_open'),
                                  icon: <ExternalLink size={15} aria-hidden="true" />,
                                  onSelect: () => navigate(`/workers/${w.id}`),
                                },
                                {
                                  label: t('worker_deactivate'),
                                  icon: <UserX size={15} aria-hidden="true" />,
                                  danger: true,
                                  onSelect: () => void deactivate(w),
                                },
                              ]
                            : [
                                {
                                  label: t('worker_reactivate'),
                                  icon: <RotateCcw size={15} aria-hidden="true" />,
                                  onSelect: () => reactivate(w),
                                },
                                {
                                  label: t('worker_open'),
                                  icon: <ExternalLink size={15} aria-hidden="true" />,
                                  onSelect: () => navigate(`/workers/${w.id}`),
                                },
                              ]
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SlideOver
        open={adding}
        title={t('workers_add')}
        subtitle={t('workers_add_sub')}
        onClose={() => setAdding(false)}
      >
        <WorkerForm
          code={nextCode}
          designations={designations}
          mode="create"
          values={form}
          errors={fieldErrors}
          busy={busy}
          onChange={(next, clear) => {
            setForm(next);
            if (clear) setFieldErrors((fe) => ({ ...fe, [clear]: null }));
          }}
          onSubmit={create}
          onCancel={() => setAdding(false)}
        />
      </SlideOver>
    </div>
  );
}
